import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Alert, Button, Calendar, Col, Flex, Row, Select, Space } from "antd";
import dayjs from 'dayjs';
import _ from 'lodash-es';
import {useEffect, useState} from "react";
import intl from 'react-intl-universal';

import Evisit from "../../../libs/Evisit";
import request from "../../../libs/request";

const getSlotsParameters = (intake, providerId, data) => {
    // retrieve from server if availableSlots is not there
    var req = {};
    var today = Math.floor(Date.now() / 10000) * 10;
    req.start = today;
    req.duration = 30 * 24 * 60 * 60;

    if (intake.id) {
        req.intake_id = intake.id;
    }
    if (_.get(data, "room.value")) {
        req.room_id = data.room.value.id;
    }
    if (_.get(data, "intake.value.consultation.id")) {
        req.consultation_id = data.intake.value.consultation.id;
    }
    if (providerId) {
        req.provider_id = providerId;
    }
    req.reservation_creator = _.get(window, "user.token", _.get(window, 'CONFIG.Account.waiting_room.visits.appointment_v2.guest_user', ''));
    return req;
}

const anyProviderOption = () => {
    return {
        full_name: _.get(window.CONFIG, "Account.waiting_room.show_calendar_by_provider.default_option", intl.get("any_p").d("Any provider")),
        id: "",
        value: ""
    }
}

const providerOptions = (providers) => {
    var providersData = _.filter(providers, function(provider) {
        return _.get(window.CONFIG, 'Account.waiting_room.visits.appointment.users.provider.enable_selection.subtypes', [window.CONST.USER.SUBTYPE.PROVIDER.DOCTOR]).indexOf(parseInt(provider.subtype)) >= 0;
    });
    providersData.unshift(anyProviderOption());
    return providersData;
}

const CalendarPicker = ({currentComponent, onNext, onPrevious, room, reservationRef, data, isMobileWebview}) => {
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState({});
    const [selectedProvider, setSelectedProvider] = useState("");
    const [firstDay, setFirstDay] = useState(dayjs());
    const [selectedDay, setSelectedDay] = useState(dayjs().startOf("d").unix());
    const [loading, setLoading] = useState(false);
    const intakeData = _.get(data, "intake.value", {});
    const providers = _.get(data, "providers.value");

    const loadAvailabilities = async () => {
        var params = getSlotsParameters(intakeData, selectedProvider, data);
        params.nearest = true;
        const response = await request.get(`${window.base_url}/api_v3/availability`, {params});

        var slotData = [];
        var slotsInResponse = _.get(response, "data.slots", _.get(response, 'data', []));
        for (var i in slotsInResponse) {
            var slot = slotsInResponse[i];
            var slotStart = _.get(slot, 'slot_start', _.get(slot, 'start'));
            var slotBooked = _.get(slot, 'slot_booked', _.get(slot, 'booked'));
            var slotAvailable = _.get(slot, 'slot_available', _.get(slot, 'slots'));

            var bookBlackout = _.get(room, "visits.appointment.book_blackout", window.CONST.SLOT.TIME.APPOINTMENT.BLACKOUT);

            // if slot started before 1 hour from now && not allow booking of past appointment, do not display the appointment
            if (dayjs.unix(slotStart).isBefore(dayjs().add('second', bookBlackout)) && !window.CONFIG.Visit.AllowPastAppointmentBooking) {
                continue;
            }

            if (slotBooked >= slotAvailable) {
                continue;
            }
            slot.start = slotStart; // Unify the field name 'start' / 'slot_start'
            slotData.push(slot);
        }
        setSlots(slotData);
        if (slotData.length) {
            if (currentComponent.value) {
                setSelectedSlot({
                    start: currentComponent.value.slot_start
                })
            }
            setSelectedDay(slotData[0].start);
            setFirstDay(dayjs.unix(slotData[0].start));
        }
        
    }

    const onCalendarChange = (date) => {
        setSelectedDay(date);
        var ts = date.unix();
        if (selectedDay && selectedDay !== ts) {
            setSelectedDay(ts);
        }
    }

    const onSubmit = async () => {
        setLoading(true);
        var data = {
            slot_start: _.get(selectedSlot, "start", _.get(selectedSlot, "slot_start")),
            slot_end: _.get(selectedSlot, "end", _.get(selectedSlot, "slot_end")),
        }
        if (selectedProvider) {
            data["provider_id"] = selectedProvider;
        }
        if (reservationRef.current) {
            reservationRef.current.reserve(selectedSlot, room, selectedProvider);
        }
        onNext(data);
    };

    const selectSlot = (slot) => {
        setSelectedSlot(slot);
    }

    const renderSlots = () => {
        var slotForDate = _.filter(slots, function (slot) {
            var slotDateStr = dayjs.unix(slot.start).format('DD-MM-YYYY');
            var dateStr = dayjs.unix(selectedDay).endOf('day').format('DD-MM-YYYY');
            return slotDateStr === dateStr;
        });
        if (slotForDate.length === 0) {
            return <Col>
                    <Alert 
                        message={intl.get("no_slots").d("There are no slots available. Please select another date or provider")}
                        type="error"
                    />
                </Col>
            ;
        }

        var slotItems = [];
        for (let i in slotForDate) {
            slotItems.push(
                <Col md={6} key={slotForDate[i]["start"]}>
                    <div
                        data-test={slotForDate[i]["start"]}
                        className={"slot-item" + (_.get(selectedSlot, "start") === slotForDate[i]["start"] ? " selected" : "")}
                        onClick={() => selectSlot(slotForDate[i])}
                    >
                        {dayjs.unix(slotForDate[i]["start"]).format("hh:mm A")}
                    </div>
                </Col>
            );
        }
        return slotItems;
    }

    const availableDays = () => {
        return _.uniq(_.map(slots, (slot) => {
            return dayjs.unix(slot["start"]).format("YYYY-MM-DD");
        }));
    }

    const disabledDate = (date) => {
        return availableDays().indexOf(dayjs(date).format('YYYY-MM-DD')) < 0;
    }

    useEffect(() => {
        loadAvailabilities();
        const timeoutId = setInterval(() => {
            if (!document.hidden) {
                loadAvailabilities();
            }
        }, 60000);
        return () => clearTimeout(timeoutId);
    }, [selectedProvider]);

    return (
        <Space id="CalendarPicker" className="calendar-picker" direction="vertical" size="middle" style={{ display: 'flex' }}>
            <Row gutter={[10,0]}>
                <Col xs={24} lg={12}>
                    <label className="field-label">{ intl.get("date").d("Date") }</label>
                    <div>
                        <Calendar
                            fullscreen={false}
                            disabledDate={disabledDate}
                            onChange={onCalendarChange}
                            mode='month'
                            className='intake-calendar-picker'
                        />
                    </div>
                </Col>
                <Col xs={24} lg={12}>
                    <label className="field-label">{ Evisit.t('provider', [window.CONST.CAPITAL]) }</label>
                    <Select
                        style={{ width: '100%' }}
                        options={providerOptions(providers)}
                        fieldNames={{
                            label: "full_name",
                            value: "id"
                        }}
                        value={selectedProvider}
                        onChange={(value) => {
                            setSelectedProvider(value);
                        }}
                    />

                    <label className="field-label">{ intl.get("time").d("Time") }</label>
                    <Row>
                        { renderSlots() }
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Flex justify={isMobileWebview ? "center" : "space-between"}>
                        { (onPrevious != null && !isMobileWebview) ?
                            <Button onClick={ onPrevious }>
                                <LeftOutlined style={{verticalAlign:"middle"}} />
                                { intl.get("back").d("Back") }
                            </Button> : <div></div>
                        }
                        <Button
                            type="primary"
                            shape={isMobileWebview ? "round" : "default"}
                            size={isMobileWebview ? "large" : "default"}
                            onClick={() => onSubmit()} loading={loading}
                            disabled={_.isEmpty(selectedSlot)}
                        >
                            {intl.get("continue").d("Continue")}
                            <RightOutlined style={{verticalAlign:"middle"}} />
                        </Button>
                    </Flex>
                </Col>
            </Row>
        </Space>
    );
};

export default CalendarPicker;